/* eslint-disable no-unreachable, no-debugger */
import PubSub from 'pubsub-js';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createProjectApi, exportProjectApi, saveProjectApi } from '../../../../api/curator';
import { ROOM_TYPE } from '../../../../constants/projectConstants';
import { errorToast, infoToast } from '../../../../helpers/toastHelper';
import { getCuratorPath, getProjectsPath } from '../../../../helpers/urlHelper';
import { getAppState } from '../../../../redux/store';
import {
  saveAsNew,
  SAVE_PROJECT_MODE,
  selectCalculatedRoomId,
  selectIndividualProjectData,
  selectRoomData,
  selectSaveProjectLoading,
  selectSaveProjectMode,
  selectUnityData,
  setIsV1UploadedProject,
  setPreventResetDuringUrlChange,
  setSaveProjectDialogOpen,
  setSaveProjectLoading,
  setSaveProjectMode,
  unblockProjectInitialization,
} from '../../../../redux/slicers/admin/curatorSlicer';
import { useUnityContext } from '../../../container/unityContainer';
import { useCuratorData } from '../../../../hooks/useCuratorData';
import {
  selectExportProjectLoading,
  setExportProjectLoading,
} from '../../../../redux/slicers/admin/curatorLoaderSlicer';
import { base64ToBlob, duplicateArrayRemove } from '../../../../helpers/jsHelper';
import { UnityEvent } from '../../../../helpers/unityPubSub';
import {
  selectCreatedPreviewList,
  selectCreatedRenderList,
} from '../../../../redux/slicers/admin/paginatedRenderPreviewsSlicer';
import { checkIfMemberHaveEditorAccess } from '../../../../contexts/UserContext';
import { setCuratorNotSaved, setCuratorSaved } from '../../../../pages/admin/curator/curatorState';
import { download } from '../../../../common/DownloadFileController/downloadFile';
import { canExportProject } from '../../../../helpers/unity/canSaveProject';
//import { captureException } from '@sentry/react';
import { getRoomJsonFile } from '../../../../pages/admin/curator/getRoomJsonFile';
import { counterAfterSave, resetCounter } from '../../../../pages/admin/curator/curatorAutosave';
import { selectBackupData } from '../../../../redux/slicers/admin/curatorBackupSlicer';
import { CLOSED_CURATOR_WINDOW } from '../../../../constants/productConfiguratorConstants';
import { isRetailConfigurator } from '../../../Configurator/utils';

export const addIdsToRequestData = ({
  data,
  roomInfo,
  includedIds = {
    texture: false,
    textures: true,
    layout: true,
    materials: true,
    props: true,
    products: false,
  },
  keepUniqueIds = {
    props: false,
    products: false,
  },
}) => {
  if (isRetailConfigurator()) {
     return null;
  }
  const unityData = selectUnityData(getAppState());
  const isFormData = data instanceof FormData;

  if (!unityData) {
    //captureException('UnityData is missing LF_lkj32k4823');
    return;
  }

  if (!roomInfo) {
    //captureException('roomInfo is missing LF_lkj32k48233');
    return;
  }

  // layout
  if (includedIds.layout && unityData.layoutId) {
    if (isFormData) {
      data.append('layout', unityData.layoutId);
    } else {
      data.layout = unityData.layoutId;
    }
  }

  // props
  if (includedIds.props || includedIds.products) {
    const key = includedIds.props ? 'props' : 'products';
    const propIds = roomInfo?.blenderAssetBundleDatas?.map((item) => item.id);
    const groupPropIds = roomInfo?.groupPropsMapping
      ? Object.keys(roomInfo?.groupPropsMapping)
      : [];
    let ids = [...propIds, ...groupPropIds];
    if (isFormData) {
      if (keepUniqueIds?.props || keepUniqueIds?.products) {
        ids = duplicateArrayRemove(ids);
      }
      ids?.forEach((id) => {
        data.append(key, id);
      });
    } else {
      data[key] = ids;
    }
  }

  // textures and materials
  const textureIds = [];
  const materialIds = [];
  roomInfo?.materialDetails?.map((md) => {
    if (md.loadedMaterialId) {
      materialIds.push(md.loadedMaterialId);
    }

    ['diffuseId', 'AOId', 'metallicId', 'normalId', 'roughnessId', 'transparencyId'].forEach(
      (key) => {
        if (md[key]) {
          textureIds.push(md[key]);
        }
      }
    );
  });

  if (includedIds.textures) {
    const uniqueTextureIds = duplicateArrayRemove(textureIds);
    if (isFormData) {
      uniqueTextureIds.forEach((id) => {
        data.append('textures', id);
      });
    } else {
      data.textures = uniqueTextureIds;
    }
  }

  if (includedIds.texture) {
    const uniqueTextureIds = duplicateArrayRemove(textureIds);
    if (isFormData) {
      uniqueTextureIds.forEach((id) => {
        data.append('texture', id);
      });
    } else {
      data.texture = uniqueTextureIds;
    }
  }

  if (includedIds.materials) {
    const uniqueMaterialIds = duplicateArrayRemove(materialIds);
    if (isFormData) {
      uniqueMaterialIds.forEach((id) => {
        data.append('materials', id);
      });
    } else {
      data.materials = uniqueMaterialIds;
    }
  }
};

export const useSaveProject = () => {
  const { roomId, isRoom } = useCuratorData();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cameraModule } = useUnityContext();
  const [searchParams] = useSearchParams();
  const combinedWithConfiguratorId = searchParams.get('combinedWithConfiguratorId');

  // TODO: move to seprate file

  const getProjectThumbnail = async () => {
    return new Promise((resolve) => {
      PubSub.subscribeOnce(UnityEvent.OnReceieveCurrentCameraScreenshot, (_, imageData) => {
        // TODO: refactor this code
        const blob = base64ToBlob({ imageString: imageData });
        resolve(blob);
      });
      cameraModule.GetCurrentCameraScreenshotReceiver();
    });
  };

  const saveProject = async (data, config = {}) => {
    const loading = selectSaveProjectLoading(getAppState());
    if (loading) return;

    const _saveProjectMode = config.saveProjectMode || selectSaveProjectMode(getAppState());

    if (_saveProjectMode === SAVE_PROJECT_MODE.SAVE || _saveProjectMode === SAVE_PROJECT_MODE.NEW) {
      dispatch(setSaveProjectLoading(true));
    }

    const roomJsonFile = await getRoomJsonFile();
    if (!roomJsonFile) {
      setCuratorNotSaved();
      dispatch(setSaveProjectDialogOpen(false));
      dispatch(setIsV1UploadedProject(false));
      dispatch(setSaveProjectLoading(false));
      return;
    }

    var formObj = new FormData();
    formObj.append('file', roomJsonFile.blob, 'newproject.json');

    if (data) {
      formObj.append('name', data.name);
      formObj.append('project_type', data.projectType);

      if (data.projectType !== ROOM_TYPE.SILO) {
        const filterOptions = [];
        Object.keys(data).forEach((key) => {
          if (key.startsWith('filterLabel__')) {
            filterOptions.push(...data[key].map((d) => d.value));
          }
        });
        const filterOptionsStr = filterOptions.join(',');
        formObj.append('filteroptions', filterOptionsStr);
      }
    } else {
      const individualProjectData = selectIndividualProjectData(getAppState());
      formObj.append('name', individualProjectData?.name);
      formObj.append('project_type', individualProjectData?.project_type);
      const filterIds = individualProjectData?.filteroptions?.map((o) => o.id).join(',');
      formObj.append('filteroptions', filterIds || '');
    }

    addIdsToRequestData({ data: formObj, roomInfo: roomJsonFile.raw });
    const calculatedRoomId = selectCalculatedRoomId(getAppState());
    if (combinedWithConfiguratorId && !calculatedRoomId) {
      formObj.append('rooms', combinedWithConfiguratorId);
    } else {
      formObj.append('rooms', calculatedRoomId);
    }

    try {
      // thumbnail
      const thumbnail = await getProjectThumbnail();
      formObj.append('thumbnail', thumbnail, 'project-thumbnail.jpg');
    } catch (error) {
      //captureException(error);
      console.error(error);
    }

    console.log('SAVE_PROJECT_FLOW REQUEST_DATA', formObj);
    console.log('SAVE_PROJECT_FLOW ROOM_JSON', roomJsonFile.raw);

    // connect new renders and previews to current/new project
    const createdPreviewList = selectCreatedPreviewList(getAppState());
    const createdRenderList = selectCreatedRenderList(getAppState());
    const renderPreviewList = [...createdPreviewList, ...createdRenderList];
    renderPreviewList.forEach((renderPreviewItem) => {
      if (renderPreviewItem.__IS_FROM_RENDER__) return;

      formObj.append('project_render', renderPreviewItem.id);
    });

    const individualProjectData = selectIndividualProjectData(getAppState());

    if (config?.lightRoomId) {
      localStorage.setItem(CLOSED_CURATOR_WINDOW, 'true');
    }

    switch (_saveProjectMode) {
      case SAVE_PROJECT_MODE.CLOSE_CURATOR: {
        const onSuccess = () => {
          setCuratorSaved();
          dispatch(setSaveProjectDialogOpen(false));
          navigate(getProjectsPath());
        };

        if (isRoom) {
          dispatch(createProjectApi(formObj, { onSuccess }));
        } else {
          const backupData = selectBackupData(getAppState());
          if (backupData) {
            formObj.append('autosave_id', backupData.id);
          }
          dispatch(saveProjectApi(formObj, roomId, { onSuccess }));
        }
        break;
      }
      case SAVE_PROJECT_MODE.SAVE:
        dispatch(
          saveProjectApi(formObj, individualProjectData.id, {
            onSuccess: () => {
              setCuratorSaved();
              dispatch(setSaveProjectDialogOpen(false));
              dispatch(setIsV1UploadedProject(false));
              if (counterAfterSave > 0) {
                resetCounter();
              }
            },
            onError: () => {
              errorToast('Unknown error happened while saving project');
            },
          })
        );
        break;
      case SAVE_PROJECT_MODE.NEW: {
        const backupData = selectBackupData(getAppState());
        if (backupData) {
          formObj.append('autosave_id', backupData.id);
        }

        dispatch(
          createProjectApi(formObj, {
            onSuccess: (data) => {
              setCuratorSaved();
              dispatch(setPreventResetDuringUrlChange(true));
              dispatch(saveAsNew({ project: data.data }));
              dispatch(setSaveProjectDialogOpen(false));
              navigate(getCuratorPath(data.data.id, 'project'));
              if (counterAfterSave > 0) {
                resetCounter();
              }

              setTimeout(() => {
                dispatch(unblockProjectInitialization());
              }, 200);
            },
          })
        );
        break;
      }
      default:
        throw new Error('Save project mode not supported');
    }
  };

  const openSaveProjectDialog = () => {
    dispatch(setSaveProjectMode(SAVE_PROJECT_MODE.SAVE));
    dispatch(setSaveProjectDialogOpen(true));
  };

  const openSaveProjectDialogAsNew = () => {
    if (!checkIfMemberHaveEditorAccess()) return;
    dispatch(setSaveProjectMode(SAVE_PROJECT_MODE.NEW));
    dispatch(setSaveProjectDialogOpen(true));
  };

  const exportProject = async () => {
    const canExport = await canExportProject();
    if (!canExport) {
      return;
    }

    const loading = selectExportProjectLoading(getAppState());
    const roomData = selectRoomData(getAppState());
    const individualProjectData = selectIndividualProjectData(getAppState());

    if (loading) {
      infoToast('Project export is still in progress');
      return;
    }

    setExportProjectLoading(true);
    // TODO: use info toast here
    infoToast('Please wait... Project is being exported');

    const roomJsonFile = await getRoomJsonFile();

    if (!roomJsonFile) {
      setExportProjectLoading(false);
      return;
    }

    const formData = new FormData();
    console.log('roomJsonFile', roomJsonFile);
    // console.log("roomJsonFile roomData: ", roomData)
    // console.log("roomJsonFile individualProjectData: ", individualProjectData)
    formData.append('json_file', roomJsonFile.blob, 'project.json');
    addIdsToRequestData({
      data: formData,
      roomInfo: roomJsonFile.raw,
      includedIds: {
        texture: true,
      },
    });

    const name = individualProjectData?.name || roomData?.name || 'project';
    try {
      const thumbnail = await getProjectThumbnail();
      formData.append('screenshot', thumbnail, `${name}-Reference.jpg`);
    } catch (e) {
      //captureException(e);
      console.error(e);
    }

    const fileName = `${name}.zip`;
    const fileUrl = await dispatch(exportProjectApi(formData, { fileName }));

    setExportProjectLoading(false);
    if (fileUrl) {
      await download({ fileUrl, fileName });
    }
  };

  return {
    exportProject,
    saveProject,
    openSaveProjectDialog,
    openSaveProjectDialogAsNew,
    addIdsToRequestData,
    getProjectThumbnail
  };
};
